import { useState, type FormEvent } from 'react';
import { useEnvVarMutations } from '@/api/hooks';
import type { EnvVar, EnvVarRevealResponse } from '@/api/schemas';
import { formatDateTime } from '@/lib/format';
import { CopyButton } from '@/components/CopyButton';
import { FormField, fieldA11y } from '@/components/FormField';
import { Icon } from '@/components/Icon';
import { ProblemAlert } from '@/components/ProblemAlert';
import { Tag, VisibilityBadge } from '@/components/badges';
import { Button } from '@/components/ui/button';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { revealReasonError, revealStep, revealedValue } from './revealModel';

/** Audited reveal (plain/sensitive only): asks for the reason, shows the decrypted value once with a copy button. */
export function RevealDialog({ envVar, onOpenChange }: { envVar: EnvVar | null; onOpenChange: (o: boolean) => void }) {
  return (
    <Dialog open={Boolean(envVar)} onOpenChange={onOpenChange}>
      <DialogContent data-testid="env-reveal-dialog" aria-describedby="env-reveal-desc">
        {envVar ? <RevealBody key={envVar.id} envVar={envVar} onClose={() => onOpenChange(false)} /> : null}
      </DialogContent>
    </Dialog>
  );
}

function useReveal(envVar: EnvVar) {
  const { reveal } = useEnvVarMutations();
  const [reason, setReason] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState<EnvVarRevealResponse | null>(null);
  const error = revealReasonError(reason, submitted);
  const secret = envVar.visibility === 'secret';

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    if (error || secret) return;
    try {
      setResult(await reveal.mutateAsync({ id: envVar.id, reason: reason.trim() }));
    } catch {
      /* rendered by ProblemAlert */
    }
  };
  return { reason, setReason, result, error, secret, onSubmit, isPending: reveal.isPending, revealError: reveal.error };
}

function RevealBody({ envVar, onClose }: { envVar: EnvVar; onClose: () => void }) {
  const r = useReveal(envVar);
  const step = revealStep(r.secret, r.result !== null);
  return (
    <form onSubmit={(e) => void r.onSubmit(e)} noValidate className="flex min-h-0 flex-col">
      <RevealHeader envVar={envVar} secret={r.secret} />
      <DialogBody className="flex flex-col gap-4">
        {step === 'secret' ? <SecretNotice fingerprint={envVar.currentVersion?.valueFingerprint} /> : null}
        {step === 'revealed' && r.result ? <RevealedValue result={r.result} /> : null}
        {step === 'ask' ? <ReasonField reason={r.reason} onChange={r.setReason} error={r.error} /> : null}
        <ProblemAlert error={r.revealError} testId="env-reveal-problem" />
      </DialogBody>
      <DialogFooter>
        <Button type="button" variant="ghost" onClick={onClose}>
          Close
        </Button>
        {step === 'ask' ? (
          <Button type="submit" data-testid="env-reveal-submit" loading={r.isPending}>
            <Icon name="visibility" size={18} />
            Reveal
          </Button>
        ) : null}
      </DialogFooter>
    </form>
  );
}

function RevealHeader({ envVar, secret }: { envVar: EnvVar; secret: boolean }) {
  return (
    <DialogHeader>
      <DialogTitle className="flex flex-wrap items-center gap-2">
        <span className="font-mono">{envVar.key}</span>
        <VisibilityBadge visibility={envVar.visibility} />
        {envVar.currentVersion ? <Tag>v{envVar.currentVersion.version}</Tag> : null}
      </DialogTitle>
      <DialogDescription id="env-reveal-desc">{secret ? 'Secret values are write-only and never leave Hangar except towards a build.' : 'Reveals are audited with your reason.'}</DialogDescription>
    </DialogHeader>
  );
}

function SecretNotice({ fingerprint }: { fingerprint: string | null | undefined }) {
  return (
    <p data-testid="env-reveal-secret" className="flex items-start gap-2 rounded-md border border-failed/30 bg-failed/10 px-2.5 py-2 text-[13px] text-failed">
      <Icon name="lock" size={16} className="mt-px" />
      <span>
        <code className="mr-1 font-mono text-[11px]">HGR-SECRET-NOT-REVEALABLE</code>
        This variable is secret: the API refuses to reveal it. Compare it by fingerprint ({fingerprint ?? '—'}) or create a new version.
      </span>
    </p>
  );
}

function RevealedValue({ result }: { result: EnvVarRevealResponse }) {
  const value = revealedValue(result);
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-subtle">
          Revealed {formatDateTime(result.revealedAt)} · v{result.version} · {result.valueType}
        </span>
        <CopyButton text={value} testId="env-reveal-copy" />
      </div>
      <pre data-testid="env-reveal-value" className="max-h-64 overflow-auto whitespace-pre-wrap break-all rounded-lg border bg-background p-3 font-mono text-[13px] text-foreground">
        {value}
      </pre>
    </div>
  );
}

function ReasonField({ reason, onChange, error }: { reason: string; onChange: (v: string) => void; error: string | undefined }) {
  return (
    <FormField id="env-reveal-reason" label="Reason" required error={error}>
      <Input data-testid="env-reveal-reason" value={reason} onChange={(e) => onChange(e.target.value)} placeholder="Debugging staging login (ST-601)" autoFocus maxLength={500} {...fieldA11y('env-reveal-reason', error)} />
    </FormField>
  );
}
